import { useSymbolStore } from '../store/symbolStore';
import { isVestFeedSymbol, isVestCryptoFeedSymbol } from './vestFeed';

/**
 * Canonical symbol normalizer. Converts any symbol form to its base symbol.
 * Examples:
 *   MNQM6.CME -> MNQ
 *   NQH6.CME -> NQ
 *   BTCUSD_PERP_BINANCE -> BTCUSD_PERP_BINANCE
 *   ETHUSD-BITFINEX -> ETHUSD-BITFINEX
 *   SOLUSDT-BMEX -> SOLUSDT-BMEX
 *   SILH6.COMEX -> SIL
 *   MNQ -> MNQ
 *   mnq -> MNQ
 *   BTC-PERP.VEST -> BTC-PERP.VEST (Vest chart feed: kept whole, it is its own base)
 */
export function normalizeToBase(symbol: string): string {
  if (!symbol) return '';
  const upper = symbol.toUpperCase();
  if (isVestFeedSymbol(upper)) return upper.trim();

  // Strip exchange suffix
  const withoutExchange = upper.replace(/\.(CME|COMEX|NYMEX|CBOT|NYBOT|KCBT|MGE)$/i, '');

  // Crypto symbols contain underscore or dash -- return as-is
  if (withoutExchange.includes('_') || withoutExchange.includes('-')) {
    return withoutExchange;
  }

  // Try symbol registry (longest base first to avoid MNQ matching before MNQX)
  const registrySymbols = useSymbolStore.getState().symbols
    .map(s => s.base.toUpperCase())
    .sort((a, b) => b.length - a.length);

  for (const base of registrySymbols) {
    if (withoutExchange.startsWith(base)) {
      return base;
    }
  }

  // Fallback: strip trailing contract month (letter + 1-2 digits)
  const stripped = withoutExchange.replace(/[A-Z]\d{1,2}$/, '');
  return stripped || withoutExchange;
}

/**
 * Check if a symbol is a crypto instrument. Vest chart feed symbols have an
 * explicit branch: only Vest's crypto markets are crypto (AAPL-USD-PERP.VEST
 * is not), and the `-` in their names is not the reason.
 */
export function isCryptoSymbol(symbol: string): boolean {
  if (isVestFeedSymbol(symbol)) return isVestCryptoFeedSymbol(symbol);
  const upper = symbol.toUpperCase();
  const withoutExchange = upper.replace(/\.(CME|COMEX|NYMEX|CBOT|NYBOT|KCBT|MGE)$/i, '');
  return withoutExchange.includes('_') || withoutExchange.includes('-');
}

/**
 * Contract-month-free base of a symbol, derived STRUCTURALLY.
 *
 * Deliberately does not consult the symbol registry the way `normalizeToBase`
 * does. The registry match is a `startsWith` scan, and prefix matching between
 * futures roots is exactly where contract resolution goes wrong: `MNQZ6`
 * starts with `MNQ` but a naive scan ordered the other way would also accept
 * it for `NQ`, and `MESZ6` for `ES`. Stripping the contract month off the end
 * instead yields a WHOLE token — `MNQ`, `NQ`, `MES`, `ES` — which is then
 * compared with `===`, so `MNQ` can never satisfy a request for `NQ` in either
 * direction, whatever order the inputs arrive in.
 *
 * It also has to work when the registry is empty or itself stale, which is the
 * state the app is in during exactly the rollover this exists to survive.
 */
export function contractBase(symbol: string): string {
  if (!symbol) return '';
  const upper = symbol.trim().toUpperCase();

  // Vest chart feed: no contract month, the whole name is the base
  if (isVestFeedSymbol(upper)) return upper;

  // Strip exchange suffix
  const withoutExchange = upper.replace(/\.(CME|COMEX|NYMEX|CBOT|NYBOT|KCBT|MGE)$/i, '');

  // Crypto never rolls -- no contract month to strip
  if (withoutExchange.includes('_') || withoutExchange.includes('-')) {
    return withoutExchange;
  }

  // Trailing contract month: month letter + 1-2 year digits (M6, Z6, H26)
  const stripped = withoutExchange.replace(/[A-Z]\d{1,2}$/, '');
  return stripped || withoutExchange;
}

/**
 * THE contract resolver. Every place that turns a requested, saved or
 * registry-supplied symbol into the symbol we actually subscribe to goes
 * through this one function.
 *
 * `activeSymbols` is the engine's CONFIGURED symbol list (`/api/symbols`
 * `symbols`, or `server-status.symbols`) -- the contracts it is actually
 * streaming, not the `availableSymbols` roster of every SCID file on disk.
 *
 * Rules, in order:
 *   1. exact match against an active symbol            -> use it
 *   2. same base, different contract month             -> use the ACTIVE one
 *   3. no active match at all                          -> first active symbol
 *   4. nothing active                                  -> null
 *
 * Rule 2 is what makes a quarterly roll invisible: a tab that saved
 * `NQM6.CME` in June comes back in September and gets `NQZ6.CME`. Rule 3
 * deliberately never returns the request back -- handing a dead contract to
 * the engine is what makes it open a watcher on an expired SCID file and
 * freeze the chart on three-month-old candles.
 */
export function resolveActiveSymbol(
  requested: string | null | undefined,
  activeSymbols: string[],
): string | null {
  const active = (activeSymbols || []).filter(Boolean);
  if (active.length === 0) return null;

  const want = (requested || '').trim().toUpperCase();
  if (!want) return active[0];

  // 1. Exact match -- return the list's own casing, not the caller's.
  const exact = active.find((s) => s.toUpperCase() === want);
  if (exact) return exact;

  // 2. Same base, rolled contract.
  const wantBase = contractBase(want);
  if (wantBase) {
    const rolled = active.find((s) => contractBase(s) === wantBase);
    if (rolled) return rolled;
  }

  // 3. Unknown base -- an active symbol, never the stale request.
  return active[0];
}
